import { Box, Button, Dialog, Typography } from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getSelectedPropertyCatalogId } from 'entities/Property/model/selectors/PropertySelectors';
import { CatalogItemCategoryFormBody } from 'features/AddCatalogItem/ui/AddCatalogItem/CatalogItemCategoryFormBody';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { DialogBoxContent } from 'shared/ui/DialogBoxContent/DialogBoxContent';
import { Loader } from 'shared/ui/Loader/Loader';

import { changeCatalogItemCategoryService } from '../../model/services/changeCatalogItemCategoryService';

import type { DerivedChangeCatalogItemCategoryState } from '../../lib/deriveChangeCatalogItemCategoryState';
import type { CatalogCategoryChangeFollowUp } from '../../lib/types';

interface FormValues {
    category: string;
}

export interface ChangeCatalogItemCategoryDialogProps {
    changeCatalogState: DerivedChangeCatalogItemCategoryState;
    onAfterCatalogCategoryChange?: CatalogCategoryChangeFollowUp;
    onClose: () => void;
}

export const ChangeCatalogItemCategoryDialog = ({
    changeCatalogState,
    onAfterCatalogCategoryChange,
    onClose,
}: ChangeCatalogItemCategoryDialogProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const catalogId = useSelector(getSelectedPropertyCatalogId);

    const {
        catalogItemIds,
        itemDisplayLabel,
        initialFormCategoryId,
        useAddCategoryTitle,
    } = changeCatalogState;

    const title = useAddCategoryTitle ? t('Add Category') : t('Edit Category');

    const handleSubmit = async (values: FormValues): Promise<void> => {
        await dispatch(
            changeCatalogItemCategoryService({
                catalogId,
                itemIds: catalogItemIds,
                categoryId: values.category,
            }),
        ).unwrap();

        await onAfterCatalogCategoryChange?.();

        onClose();
    };

    return (
        <Dialog open onClose={onClose}>
            <Formik<FormValues>
                enableReinitialize
                initialValues={{ category: initialFormCategoryId }}
                onSubmit={handleSubmit}
            >
                {(props: FormikProps<FormValues>) => (
                    <Form>
                        <DialogBoxContent
                            onClose={onClose}
                            buttons={
                                <Box
                                    data-testid="change-catalog-item-category-actions-block"
                                    display="flex"
                                    justifyContent="flex-end"
                                    width="100%"
                                    gap="8px"
                                >
                                    <Button onClick={onClose}>
                                        {t('Cancel')}
                                    </Button>
                                    <Button
                                        disabled={
                                            !props.values.category ||
                                            props.isSubmitting
                                        }
                                        startIcon={
                                            props.isSubmitting && (
                                                <Loader color="inherit" />
                                            )
                                        }
                                        variant="contained"
                                        type="submit"
                                    >
                                        {t('Save')}
                                    </Button>
                                </Box>
                            }
                        >
                            <Typography
                                data-testid="change-catalog-item-category-title"
                                typography={'poppins.subtitle2'}
                                mb="16px"
                            >
                                {title}
                            </Typography>
                            <Typography
                                data-testid="change-catalog-item-category-item-name"
                                typography="openSans.body2"
                                mb="36px"
                                sx={{ color: '#363636' }}
                            >
                                {itemDisplayLabel}
                            </Typography>
                            <CatalogItemCategoryFormBody
                                subtitleOverride={itemDisplayLabel}
                            />
                        </DialogBoxContent>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};
